import { useState } from 'react';
import { FaPaperclip, FaFileUpload, FaDownload, FaTrash, FaEdit, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import useSectionCollapse from '@/hooks/useSectionCollapse';

/**
 * AttachmentsSection Component
 *
 * Renders a collapsible attachments section for an action item with upload and download
 * capabilities.  Collapse state is persisted to localStorage via `useSectionCollapse` so
 * the user's preference (expanded or collapsed) is remembered across page reloads.
 *
 * @param {Object}           props
 * @param {Array}            props.attachments              - Array of attachment objects.
 * @param {boolean}          props.loadingAttachments       - Whether attachments are loading.
 * @param {boolean}          props.uploadingFile            - Whether a file is being uploaded.
 * @param {React.RefObject}  props.fileInputRef             - Ref for the hidden file input.
 * @param {Function}         props.onFileUpload             - Handler for file selection.
 * @param {Function}         props.onDeleteAttachment       - Handler for deleting an attachment.
 * @param {Function}         props.formatFileSize           - Formats raw byte count to a human-readable string.
 * @param {Function}         props.formatCommentDateTime    - Formats a date/time string for display.
 * @param {Function}         [props.onAnnotatePdf]          - Handler for opening the PDF annotation modal.
 * @param {Function}         [props.onDownloadAttachment]   - Authenticated download handler that receives
 *                                                           the attachment object and performs a secure
 *                                                           blob-based save-as flow using the JWT token.
 *                                                           When not provided the component falls back to
 *                                                           a plain anchor-tag download (no auth header).
 * @param {boolean}          [props.defaultCollapsed=true]  - Initial collapsed state used the first time
 *                                                           the user visits this page.  Defaults to true
 *                                                           (collapsed) to keep the detail page compact.
 *                                                           Once the user toggles the section their choice
 *                                                           is persisted and this prop has no further effect.
 */
const AttachmentsSection = ({
    attachments,
    loadingAttachments,
    uploadingFile,
    fileInputRef,
    onFileUpload,
    onDeleteAttachment,
    formatFileSize,
    formatCommentDateTime,
    onAnnotatePdf,
    onDownloadAttachment,
    defaultCollapsed = true
}) => {
    // Track which attachment is currently being downloaded to show per-item loading state
    const [downloadingId, setDownloadingId] = useState(null);

    // Persistent collapsed state — the user's last toggle is remembered in localStorage
    // under the key 'action-item-attachments'.  Keep this key constant across deploys;
    // changing it would silently reset every user's saved preference.
    const [isCollapsed, toggleCollapsed] = useSectionCollapse('action-item-attachments', defaultCollapsed);

    /**
     * Check if file is a PDF
     */
    const isPdfFile = (attachment) => {
        return attachment.contentType?.toLowerCase().includes('pdf') || 
               attachment.fileName?.toLowerCase().endsWith('.pdf');
    };

    /**
     * Handles a download button click.
     * When an authenticated download handler is provided (onDownloadAttachment) it is used
     * so the JWT Bearer token is included in the request — required for the protected
     * /api/ActionItems/attachments/{id}/download endpoint.
     * Falls back to a plain anchor-tag navigation when no handler is supplied.
     *
     * @param {Event}  e          - Click event (stopped from propagating).
     * @param {Object} attachment - The attachment to download.
     */
    const handleDownloadClick = async (e, attachment) => {
        e.preventDefault();
        e.stopPropagation();

        if (onDownloadAttachment) {
            setDownloadingId(attachment.attachmentId);
            try {
                await onDownloadAttachment(attachment);
            } finally {
                setDownloadingId(null);
            }
        } else {
            // Fallback: plain browser navigation (only works for unauthenticated URLs)
            window.open(attachment.downloadUrl, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <div className={`attachments-section ${isCollapsed ? 'collapsed' : ''}`}>
            <div className='attachments-header'>
                <button
                    type='button'
                    className='attachments-collapse-toggle'
                    onClick={() => toggleCollapsed()}
                    title={isCollapsed ? 'Expand attachments' : 'Collapse attachments'}
                >
                    {isCollapsed ? <FaChevronDown size={12} /> : <FaChevronUp size={12} />}
                    <h2>
                        <FaPaperclip size={16} /> Attachments
                        {attachments.length > 0 && (
                            <span className='attachments-count-badge'>{attachments.length}</span>
                        )}
                    </h2>
                </button>
                {/* Upload button always visible even when collapsed so users
                    can quickly attach files without expanding first */}
                <label className='upload-btn' title='Upload file (max 25MB)'>
                    <input
                        type='file'
                        ref={fileInputRef}
                        onChange={onFileUpload}
                        style={{ display: 'none' }}
                        disabled={uploadingFile}
                    />
                    <FaFileUpload size={14} />
                    {uploadingFile ? 'Uploading...' : 'Upload File'}
                </label>
            </div>

            {/* Collapsible content area */}
            {!isCollapsed && (
                <>
                    {loadingAttachments ? (
                        <div className='attachments-loading'>Loading attachments...</div>
                    ) : attachments.length === 0 ? (
                        <div className='no-attachments'>No files attached to this action item.</div>
                    ) : (
                        <div className='attachments-list'>
                            {attachments.map(att => (
                                <div className='attachment-item' key={att.attachmentId}>
                                    <div className='attachment-info'>
                                        <span className='attachment-name'>{att.fileName}</span>
                                        <span className='attachment-meta'>
                                            {att.fileSizeFormatted || formatFileSize(att.fileSize)} • 
                                            Uploaded by {att.uploadedByUserName} • 
                                            {formatCommentDateTime(att.uploadedAt)}
                                        </span>
                                    </div>
                                    <div className='attachment-actions'>
                                        {isPdfFile(att) && onAnnotatePdf && (
                                            <button 
                                                className='annotate-btn'
                                                onClick={() => onAnnotatePdf(att)}
                                                title='Annotate PDF'
                                            >
                                                <FaEdit size={14} />
                                            </button>
                                        )}
                                        {/* Authenticated download — uses blob fetch so the JWT token
                                            is sent in the request header instead of via the URL */}
                                        <button
                                            className='download-btn'
                                            title={downloadingId === att.attachmentId ? 'Downloading…' : 'Download file'}
                                            disabled={downloadingId === att.attachmentId}
                                            onClick={(e) => handleDownloadClick(e, att)}
                                        >
                                            <FaDownload size={14} />
                                        </button>
                                        <button 
                                            className='delete-btn'
                                            onClick={() => onDeleteAttachment(att.attachmentId)}
                                            title='Delete attachment'
                                        >
                                            <FaTrash size={14} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default AttachmentsSection;
